import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import GoogleAddressInput from '../../components/ui/GoogleAddressInput';
import type { FormattedAddress } from '../../hooks/useGoogleMapsApi';
import { 
  PaperAirplaneIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
  CheckIcon,
  ClockIcon,
  InformationCircleIcon,
  ArrowRightIcon,
  EnvelopeIcon,
  MapPinIcon,
  EyeIcon,
  UserIcon,
  PlusIcon,
  XMarkIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import StageVisualization from '../../components/lawsuit/StageVisualization';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { 
  SubmissionStage, 
  STAGE_CONFIG, 
  SUBMISSION_STAGE_ORDER 
} from '@tvia-ai/shared';
import type { Lawsuit as SharedLawsuit } from '@tvia-ai/shared';

interface StageSelection {
  stage: SubmissionStage;
  includeNextStages: boolean;
}

interface CCRecipient {
  name: string;
  email: string;
}

const EnhancedSubmissionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [step, setStep] = useState<'stage-selection' | 'verification' | 'confirmation' | 'submission'>('stage-selection');
  const [selectedStage, setSelectedStage] = useState<StageSelection>({
    stage: SubmissionStage.Complaint,
    includeNextStages: true
  });
  const [accusedPartyEmail, setAccusedPartyEmail] = useState('');
  const [accusedPartyAddress, setAccusedPartyAddress] = useState('');
  const [ccRecipients, setCcRecipients] = useState<CCRecipient[]>([]);
  const [emailVerified, setEmailVerified] = useState(false);
  const [addressVerified, setAddressVerified] = useState(false);
  const [missingDocs, setMissingDocs] = useState<string[]>([]);

  // Check if we're in dev environment
  const isDevEnvironment = window.location.href.includes('dev');

  // Fetch lawsuit details
  const { data: lawsuit, isLoading, error } = useQuery<SharedLawsuit | undefined>({
    queryKey: ['lawsuit', id],
    queryFn: async () => {
      if (!id) return undefined;
      const response = await axios.get(`/api/lawsuits/${id}`);
      return response.data.data.lawsuit as SharedLawsuit;
    },
    enabled: !!id,
  });

  // Initialize values from lawsuit data
  useEffect(() => {
    if (lawsuit) {
      // Check for accused party email from various sources
      const accusedEmail = lawsuit.accusedPartyEmail || 
                          lawsuit.aiAnalysis?.accusedParty?.contact?.split(',')[0]?.trim() ||
                          '';
      setAccusedPartyEmail(accusedEmail);
      
      // Check for accused party address
      const accusedAddress = lawsuit.accusedPartyAddress || 
                            lawsuit.aiAnalysis?.accusedParty?.address ||
                            '';
      setAccusedPartyAddress(accusedAddress);

      // Check verification status
      setEmailVerified(lawsuit.accusedPartyEmailVerified || false);
      setAddressVerified(lawsuit.accusedPartyAddressVerified || false);
      
      // Check for missing documents needed for the selected stage
      checkRequiredDocuments();
    }
  }, [lawsuit, selectedStage.stage]);

  const checkRequiredDocuments = () => {
    if (!lawsuit) return;
    
    const requiredDocTypes = getRequiredDocumentsForStage(selectedStage.stage);
    const existingDocs = lawsuit.documents?.map((doc: any) => doc.type) || [];
    const missing = requiredDocTypes.filter(type => !existingDocs.includes(type));
    setMissingDocs(missing);
  };

  const getRequiredDocumentsForStage = (stage: SubmissionStage): string[] => {
    switch (stage) {
      case SubmissionStage.Complaint:
        return ['complaint'];
      case SubmissionStage.DemandLetter:
        return ['letter'];
      case SubmissionStage.Lawsuit:
        return ['lawsuit'];
      default:
        return [];
    }
  };

  // Verify email mutation
  const verifyEmailMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await axios.post(`/api/lawsuits/${id}/verify-email`, { email });
      return response.data;
    },
    onSuccess: () => {
      setEmailVerified(true);
    },
  });

  // Verify address mutation
  const verifyAddressMutation = useMutation({
    mutationFn: async (address: string) => {
      const response = await axios.post(`/api/lawsuits/${id}/verify-address`, { address });
      return response.data;
    },
    onSuccess: () => {
      setAddressVerified(true);
    },
  });

  // Start submission mutation
  const startSubmissionMutation = useMutation({
    mutationFn: async (data: {
      startStage: SubmissionStage;
      includeNextStages: boolean;
      accusedPartyEmail: string;
      accusedPartyAddress: string;
      ccRecipients?: CCRecipient[];
    }) => {
      const response = await axios.post(`/api/lawsuits/${id}/start-submission`, data);
      return response.data;
    },
    onSuccess: () => {
      navigate(`/lawsuit/${id}?submission-started=true`);
    },
  });

  const stages = SUBMISSION_STAGE_ORDER.map(stage => {
    const config = STAGE_CONFIG[stage];
    return {
      id: stage,
      title: t(config.title, `Stage ${config.order}`),
      description: t(config.description, 'Stage description'),
      timeline: t('lawsuit.estimatedDuration', 'Est. {{days}} days', { days: config.estimatedDays }),
      recommended: t(`submission.stages.${stage}.recommended`, 'Recommended for specific cases'),
      icon: config.icon === 'DocumentTextIcon' ? DocumentTextIcon :
            config.icon === 'ExclamationTriangleIcon' ? ExclamationTriangleIcon :
            ScaleIcon,
      color: config.color,
    };
  });

  const handleVerifyEmail = () => {
    if (!accusedPartyEmail) return;
    verifyEmailMutation.mutate(accusedPartyEmail);
  };

  const handleVerifyAddress = () => {
    if (!accusedPartyAddress) return;
    verifyAddressMutation.mutate(accusedPartyAddress);
  };

  const handleStartSubmission = () => {
    startSubmissionMutation.mutate({
      startStage: selectedStage.stage,
      includeNextStages: selectedStage.includeNextStages,
      accusedPartyEmail,
      accusedPartyAddress,
      ccRecipients,
    });
  };

  const addCcRecipient = () => {
    setCcRecipients([...ccRecipients, { name: '', email: '' }]);
  };

  const removeCcRecipient = (index: number) => {
    setCcRecipients(ccRecipients.filter((_, i) => i !== index));
  };

  const updateCcRecipient = (index: number, field: keyof CCRecipient, value: string) => {
    const updated = [...ccRecipients];
    updated[index] = { ...updated[index], [field]: value };
    setCcRecipients(updated);
  };

  const canProceedToConfirmation = () => {
    return emailVerified && addressVerified && missingDocs.length === 0;
  };

  const getColorClasses = (color: string) => {
    const colorMap: Record<string, any> = {
      blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900', icon: 'text-blue-600' },
      yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-900', icon: 'text-yellow-600' },
      red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-900', icon: 'text-red-600' },
    };
    return colorMap[color] || colorMap.blue;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !lawsuit) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t('errors.lawsuitNotFound', 'Lawsuit not found')}
          </h3>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${t('submission.title', 'Start Lawsuit Submission')} - ${lawsuit.title} - tvia.ai`}</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-6 py-8">
              <div className="flex items-center mb-4">
                <PaperAirplaneIcon className="h-6 w-6 text-cyan-500 mr-3" />
                <h1 className="text-2xl font-bold text-gray-900">
                  {t('submission.title', 'Start Lawsuit Submission')}
                </h1>
              </div>
              <p className="text-gray-600 mb-4">
                {t('submission.description', 'Choose your starting stage and we\'ll guide you through our strategic submission process.')}
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-blue-900 mb-1">
                  {lawsuit.title}
                </h3>
                <p className="text-sm text-blue-800">
                  {t('lawsuit.accusedParty', 'Accused Party')}: {lawsuit.aiAnalysis?.accusedParty?.name || t('common.notSpecified', 'Not specified')}
                </p>
              </div>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className={`flex items-center ${step === 'stage-selection' ? 'text-cyan-600' : 'text-green-600'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === 'stage-selection' ? 'bg-cyan-100' : 'bg-green-100'
                  }`}>
                    {step === 'stage-selection' ? '1' : <CheckIcon className="h-4 w-4" />}
                  </div>
                  <span className="ml-2 text-sm font-medium">
                    {t('submission.steps.stageSelection', 'Select Stage')}
                  </span>
                </div>
                <div className={`flex items-center ${
                  step === 'verification' ? 'text-cyan-600' : 
                  ['confirmation', 'submission'].includes(step) ? 'text-green-600' : 'text-gray-400'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === 'verification' ? 'bg-cyan-100' : 
                    ['confirmation', 'submission'].includes(step) ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {['confirmation', 'submission'].includes(step) ? <CheckIcon className="h-4 w-4" /> : '2'}
                  </div>
                  <span className="ml-2 text-sm font-medium">
                    {t('submission.steps.verification', 'Verify Information')}
                  </span>
                </div>
                <div className={`flex items-center ${
                  step === 'confirmation' ? 'text-cyan-600' : 
                  step === 'submission' ? 'text-green-600' : 'text-gray-400'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === 'confirmation' ? 'bg-cyan-100' : 
                    step === 'submission' ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {step === 'submission' ? <CheckIcon className="h-4 w-4" /> : '3'}
                  </div>
                  <span className="ml-2 text-sm font-medium">
                    {t('submission.steps.confirmation', 'Confirm & Submit')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Step Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-white shadow rounded-lg p-8">
                {step === 'stage-selection' && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 mb-6">
                      {t('submission.selectStage.title', 'Choose Your Starting Stage')}
                    </h2>
                    <p className="text-gray-600 mb-8">
                      {t('submission.selectStage.description', 'Select the stage that best fits your situation. You can choose to include all subsequent stages in the process.')}
                    </p>

                {/* Flow Overview Link */}
                <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-blue-900">
                        {t('submission.learnMore', 'Want to understand our process better?')}
                      </h3>
                      <p className="text-sm text-blue-800">
                        {t('submission.learnMoreDescription', 'Learn about our strategic three-stage approach and how we guide you every step')}
                      </p>
                    </div>
                    <button
                      onClick={() => window.open('/lawsuit-flow', '_blank')}
                      className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50"
                    >
                      <EyeIcon className="h-4 w-4 mr-1" />
                      {t('submission.learnMore', 'Learn More')}
                    </button>
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  {stages.map((stage) => {
                    const colors = getColorClasses(stage.color);
                    const IconComponent = stage.icon;
                    const isSelected = selectedStage.stage === stage.id;
                    
                    return (
                      <div
                        key={stage.id}
                        className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
                          isSelected ? `${colors.border} ${colors.bg}` : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedStage(prev => ({ ...prev, stage: stage.id }))}
                      >
                        <div className="flex items-start space-x-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            isSelected ? colors.bg : 'bg-gray-100'
                          }`}>
                            <IconComponent className={`h-6 w-6 ${isSelected ? colors.icon : 'text-gray-500'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className={`text-lg font-semibold ${isSelected ? colors.text : 'text-gray-900'}`}>
                                {stage.title}
                              </h3>
                              {isSelected && (
                                <CheckIcon className={`h-5 w-5 ${colors.icon}`} />
                              )}
                            </div>
                            <p className={`text-sm mb-2 ${isSelected ? colors.text : 'text-gray-600'}`}>
                              {stage.description}
                            </p>
                            <div className="flex items-center justify-between">
                              <span className={`text-xs ${isSelected ? colors.text : 'text-gray-500'}`}>
                                <ClockIcon className="h-3 w-3 inline mr-1" />
                                {stage.timeline}
                              </span>
                              <span className={`text-xs font-medium ${isSelected ? colors.text : 'text-gray-500'}`}>
                                {stage.recommended}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Include Next Stages Option */}
                <div className="mb-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedStage.includeNextStages}
                      onChange={(e) => setSelectedStage(prev => ({ ...prev, includeNextStages: e.target.checked }))}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-900">
                      {t('submission.includeNextStages', 'Automatically proceed to next stages if needed')}
                    </span>
                  </label>
                  <p className="mt-2 text-xs text-gray-600 ml-7">
                    {t('submission.includeNextStagesDescription', 'If the selected stage doesn\'t resolve the issue, we\'ll automatically move to the next stage in the process.')}
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setStep('verification')}
                    disabled={!selectedStage.stage}
                    className={`px-6 py-3 rounded-md text-white font-medium ${
                      selectedStage.stage
                        ? 'bg-cyan-600 hover:bg-cyan-700'
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {t('common.continue', 'Continue')}
                    <ArrowRightIcon className="h-4 w-4 ml-2 inline" />
                  </button>
                </div>
              </div>
            )}

            {step === 'verification' && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-6">
                  {t('submission.verification.title', 'Verify Accused Party Information')}
                </h2>
                <p className="text-gray-600 mb-8">
                  {t('submission.verification.description', 'We need to verify the accused party\'s contact information to ensure successful delivery of legal documents.')}
                </p>

                {/* Dev Environment Warning */}
                {isDevEnvironment && (
                  <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex">
                      <ExclamationCircleIcon className="h-5 w-5 text-yellow-500 mr-3 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-medium text-yellow-800">
                          {t('submission.devWarning.title', 'Development Environment')}
                        </h3>
                        <p className="text-sm text-yellow-700 mt-1">
                          {t('submission.devWarning.message', 'Submissions in dev are currently mock only. No real documents will be sent.')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Missing Documents Warning */}
                {missingDocs.length > 0 && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-3 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-medium text-red-800">
                          {t('submission.missingDocs.title', 'Missing Required Documents')}
                        </h3>
                        <p className="text-sm text-red-700 mt-1">
                          {t('submission.missingDocs.message', 'The following documents are required for this stage:')}
                        </p>
                        <ul className="list-disc list-inside text-sm text-red-700 mt-2">
                          {missingDocs.map(docType => (
                            <li key={docType}>
                              {t(`documents.${docType}.name`, docType)}
                            </li>
                          ))}
                        </ul>
                        <button
                          onClick={() => navigate(`/lawsuit/${id}/generate`)}
                          className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                        >
                          <DocumentTextIcon className="h-4 w-4 mr-2" />
                          {t('submission.generateDocs', 'Generate Documents')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Accused Party Information */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <UserIcon className="h-5 w-5 text-gray-500 mr-2" />
                      <h3 className="text-lg font-medium text-gray-900">
                        {t('submission.verification.accusedParty', 'Accused Party Information')}
                      </h3>
                    </div>
                    
                    {lawsuit.aiAnalysis?.accusedParty && (
                      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          {t('submission.verification.detectedInfo', 'Detected Information')}
                        </h4>
                        <div className="text-sm text-gray-600 space-y-1">
                          {lawsuit.aiAnalysis.accusedParty.name && (
                            <p><strong>{t('common.name', 'Name')}:</strong> {lawsuit.aiAnalysis.accusedParty.name}</p>
                          )}
                          {lawsuit.aiAnalysis.accusedParty.details && (
                            <p><strong>{t('common.details', 'Details')}:</strong> {lawsuit.aiAnalysis.accusedParty.details}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Email Verification */}
                    <div className="mb-6">
                      <div className="flex items-center mb-2">
                        <EnvelopeIcon className="h-5 w-5 text-gray-500 mr-2" />
                        <label className="text-sm font-medium text-gray-900">
                          {t('submission.verification.email', 'Email Address')}
                        </label>
                        {emailVerified && (
                          <CheckIcon className="h-5 w-5 text-green-500 ml-2" />
                        )}
                      </div>
                      <div className="flex space-x-4">
                        <input
                          type="email"
                          value={accusedPartyEmail}
                          onChange={(e) => setAccusedPartyEmail(e.target.value)}
                          placeholder={t('submission.verification.emailPlaceholder', 'Enter accused party email address')}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          disabled={emailVerified}
                        />
                        <button
                          onClick={handleVerifyEmail}
                          disabled={!accusedPartyEmail || emailVerified || verifyEmailMutation.isPending}
                          className={`px-4 py-2 rounded-md text-white font-medium ${
                            emailVerified
                              ? 'bg-green-600'
                              : !accusedPartyEmail || verifyEmailMutation.isPending
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-cyan-600 hover:bg-cyan-700'
                          }`}
                        >
                          {verifyEmailMutation.isPending ? (
                            <LoadingSpinner size="sm" />
                          ) : emailVerified ? (
                            t('common.verified', 'Verified')
                          ) : (
                            t('common.verify', 'Verify')
                          )}
                        </button>
                      </div>
                      {emailVerified && (
                        <p className="mt-2 text-sm text-green-600">
                          {t('submission.verification.emailVerified', 'Email address has been verified and is deliverable')}
                        </p>
                      )}
                    </div>

                    {/* Address Verification */}
                    <div>
                      <div className="flex items-center mb-2">
                        <MapPinIcon className="h-5 w-5 text-gray-500 mr-2" />
                        <label className="text-sm font-medium text-gray-900">
                          {t('submission.verification.address', 'Mailing Address')}
                        </label>
                        {addressVerified && (
                          <CheckIcon className="h-5 w-5 text-green-500 ml-2" />
                        )}
                      </div>
                      <div className="flex space-x-4">
                        <GoogleAddressInput
                          value={accusedPartyAddress}
                          onChange={setAccusedPartyAddress}
                          onAddressSelect={(address: FormattedAddress) => {
                            setAccusedPartyAddress(address.formattedAddress);
                          }}
                          placeholder={t('submission.verification.addressPlaceholder', 'Enter complete mailing address')}
                          disabled={addressVerified}
                          className="flex-1"
                          addressType={lawsuit.aiAnalysis?.accusedParty?.isBusiness ? 'business' : 'personal'}
                        />
                        <button
                          onClick={handleVerifyAddress}
                          disabled={!accusedPartyAddress || addressVerified || verifyAddressMutation.isPending}
                          className={`px-4 py-2 rounded-md text-white font-medium h-fit ${
                            addressVerified
                              ? 'bg-green-600'
                              : !accusedPartyAddress || verifyAddressMutation.isPending
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-cyan-600 hover:bg-cyan-700'
                          }`}
                        >
                          {verifyAddressMutation.isPending ? (
                            <LoadingSpinner size="sm" />
                          ) : addressVerified ? (
                            t('common.verified', 'Verified')
                          ) : (
                            t('common.verify', 'Verify')
                          )}
                        </button>
                      </div>
                      {addressVerified && (
                        <p className="mt-2 text-sm text-green-600">
                          {t('submission.verification.addressVerified', 'Address has been verified and is valid for legal service')}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* CC Recipients */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <EnvelopeIcon className="h-5 w-5 text-gray-500 mr-2" />
                        <h3 className="text-lg font-medium text-gray-900">
                          {t('submission.verification.ccRecipients', 'CC Recipients (Optional)')}
                        </h3>
                      </div>
                      <button
                        onClick={addCcRecipient}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        {t('submission.verification.addCC', 'Add CC')}
                      </button>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-4">
                      {t('submission.verification.ccDescription', 'Add additional recipients who should receive copies of the legal documents.')}
                    </p>

                    {ccRecipients.length === 0 && (
                      <p className="text-sm text-gray-500 italic">
                        {t('submission.verification.noCCRecipients', 'No CC recipients added')}
                      </p>
                    )}

                    {ccRecipients.map((recipient, index) => (
                      <div key={index} className="flex space-x-4 mb-4 p-4 border border-gray-200 rounded-lg">
                        <input
                          type="text"
                          value={recipient.name}
                          onChange={(e) => updateCcRecipient(index, 'name', e.target.value)}
                          placeholder={t('submission.verification.ccNamePlaceholder', 'Recipient name')}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                        <input
                          type="email"
                          value={recipient.email}
                          onChange={(e) => updateCcRecipient(index, 'email', e.target.value)}
                          placeholder={t('submission.verification.ccEmailPlaceholder', 'Recipient email')}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                        <button
                          onClick={() => removeCcRecipient(index)}
                          className="px-3 py-2 text-red-600 hover:text-red-800"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between mt-8">
                  <button
                    onClick={() => setStep('stage-selection')}
                    className="px-6 py-3 rounded-md border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
                  >
                    {t('common.back', 'Back')}
                  </button>
                  <button
                    onClick={() => setStep('confirmation')}
                    disabled={!canProceedToConfirmation()}
                    className={`px-6 py-3 rounded-md text-white font-medium ${
                      canProceedToConfirmation()
                        ? 'bg-cyan-600 hover:bg-cyan-700'
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {t('common.continue', 'Continue')}
                    <ArrowRightIcon className="h-4 w-4 ml-2 inline" />
                  </button>
                </div>
              </div>
            )}

            {step === 'confirmation' && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-6">
                  {t('submission.confirmation.title', 'Confirm Submission Details')}
                </h2>

                <div className="space-y-6">
                  {/* Selected Stage */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {t('submission.confirmation.selectedStage', 'Starting Stage')}
                    </h3>
                    {(() => {
                      const stage = stages.find(s => s.id === selectedStage.stage);
                      if (!stage) return null;
                      const colors = getColorClasses(stage.color);
                      const IconComponent = stage.icon;
                      
                      return (
                        <div className={`${colors.bg} ${colors.border} border rounded-lg p-4`}>
                          <div className="flex items-center space-x-3">
                            <IconComponent className={`h-6 w-6 ${colors.icon}`} />
                            <div>
                              <h4 className={`font-medium ${colors.text}`}>{stage.title}</h4>
                              <p className={`text-sm ${colors.text}`}>{stage.description}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                    {selectedStage.includeNextStages && (
                      <p className="mt-3 text-sm text-gray-600">
                        {t('submission.confirmation.includeNextStages', 'Will automatically proceed to next stages if needed')}
                      </p>
                    )}
                  </div>

                  {/* Contact Information */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {t('submission.confirmation.contactInfo', 'Verified Contact Information')}
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <EnvelopeIcon className="h-5 w-5 text-gray-500 mr-3" />
                        <span className="text-sm text-gray-900">{accusedPartyEmail}</span>
                        <CheckIcon className="h-4 w-4 text-green-500 ml-2" />
                      </div>
                      <div className="flex items-start">
                        <MapPinIcon className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
                        <span className="text-sm text-gray-900">{accusedPartyAddress}</span>
                        <CheckIcon className="h-4 w-4 text-green-500 ml-2 mt-0.5" />
                      </div>
                    </div>
                  </div>

                  {/* Important Notice */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                    <div className="flex items-start">
                      <InformationCircleIcon className="h-6 w-6 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-semibold text-amber-900 mb-2">
                          {t('submission.confirmation.importantNotice', 'Important Notice')}
                        </h3>
                        <ul className="space-y-1 text-sm text-amber-800">
                          <li>• {t('submission.confirmation.notice1', 'Once submitted, this action cannot be undone')}</li>
                          <li>• {t('submission.confirmation.notice2', 'Documents will be sent to the accused party within 24 hours')}</li>
                          <li>• {t('submission.confirmation.notice3', 'You will receive updates on the submission progress')}</li>
                          <li>• {t('submission.confirmation.notice4', 'Legal deadlines and response periods will be automatically tracked')}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between mt-8">
                  <button
                    onClick={() => setStep('verification')}
                    className="px-6 py-3 rounded-md border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
                  >
                    {t('common.back', 'Back')}
                  </button>
                  <button
                    onClick={handleStartSubmission}
                    disabled={startSubmissionMutation.isPending}
                    className="px-6 py-3 rounded-md text-white font-medium bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                  >
                    {startSubmissionMutation.isPending ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        {t('submission.submitting', 'Starting Submission...')}
                      </>
                    ) : (
                      <>
                        <PaperAirplaneIcon className="h-4 w-4 mr-2 inline" />
                        {t('submission.startSubmission', 'Start Submission')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {step === 'submission' && (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckIcon className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('submission.success.title', 'Submission Started Successfully')}
                </h2>
                <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
                  {t('submission.success.description', 'Your lawsuit submission process has been initiated. We will begin processing your case according to the selected stage and keep you updated on all progress.')}
                </p>
                <button
                  onClick={() => navigate(`/lawsuit/${id}`)}
                  className="px-6 py-3 rounded-md text-white font-medium bg-cyan-600 hover:bg-cyan-700"
                >
                  {t('submission.success.viewCase', 'View Case Details')}
                </button>
              </div>
            )}
                </div>
              </div>
              
              {/* Sidebar with Stage Visualization */}
              <div className="lg:col-span-1">
                <div className="bg-white shadow rounded-lg p-6 sticky top-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t('lawsuit.submissionFlow', 'Submission Process')}
                  </h3>
                  <StageVisualization
                    currentStage={lawsuit.currentSubmissionStage}
                    completedStages={lawsuit.submissionStages?.filter(s => s.status === 'completed').map(s => s.stage) || []}
                    showProgress={true}
                    showNextStage={true}
                  />
                </div>
              </div>
            </div>
        </div>
      </div>
    </>
  );
};

export default EnhancedSubmissionPage;